import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "@phosphor-icons/react";
import PageHero from "@/components/PageHero";
import { FadeUp, ImageReveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import usePageMeta from "@/lib/usePageMeta";

const POINTS = [
  "Vinduestype og opdeling tilpasset husets arkitektur",
  "Træ, træ/alu og forskellige behov for vedligeholdelse",
  "Glas, energiforhold, støjreduktion og indeklima",
  "Montering og finish med respekt for det eksisterende hus",
];

export default function Windows() {
  usePageMeta({
    title: "Vinduer",
    description:
      "Vinduer valgt med blik for hele huset. Slanke rammer, energiglas og løsninger tilpasset boligens arkitektur — i samarbejde med Vindunor.",
    path: "/vinduer",
    image: "/images/skovvej-8.webp",
  });

  return (
    <div data-testid="windows-page">
      <PageHero
        eyebrow="Specialister i vinduer"
        titleLines={["Vinduer valgt", "med blik for", "hele huset"]}
        image="/images/skovvej-8.webp"
        kicker="Vinduer former både husets udtryk, lyset i rummene og komforten i hverdagen. Vi rådgiver om materialer, formater, sprosser, åbningsmuligheder og energiforhold, så du får en løsning, der passer naturligt til boligens arkitektur og dine behov."
      />

      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-7 grid grid-cols-2 gap-4 md:gap-6 order-2 md:order-1">
            <ImageReveal src="/images/skovvej-8.webp" alt="Orangeri" className="aspect-square col-span-2" />
            <ImageReveal src="/images/skovvej-20.webp" alt="Hjørnevindue mod haven" className="aspect-[3/4]" delay={0.1} />
            <ImageReveal src="/images/skovvej-22.webp" alt="Buet vindue" className="aspect-[3/4]" delay={0.2} />
          </div>
          <div className="md:col-span-5 order-1 md:order-2">
            <FadeUp>
              <p className="text-xs uppercase tracking-label text-accent mb-6">Vores tilgang</p>
              <h2 className="font-display text-4xl md:text-5xl font-light leading-tight text-balance">
                De rigtige vinduer ændrer mere end udsigten
              </h2>
              <p className="mt-6 text-muted-foreground font-light text-lg">
                Vinduer har stor betydning for både husets arkitektur, dagslyset i rummene, indeklimaet og energiforbruget. Derfor findes der ikke én standardløsning, som passer til alle boliger. Vi tager udgangspunkt i husets stil, de eksisterende åbninger og den måde, rummene bliver brugt på. Sammen afvejer vi lysindfald, materialer, rammer, åbningsmuligheder og vedligeholdelse, så du får vinduer, der fungerer i hverdagen og føles som en naturlig del af huset.
              </p>
              <ul className="mt-10 space-y-4">
                {POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-3 font-light">
                    <Check size={20} weight="light" className="text-accent mt-1 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Link to="/kontakt" data-testid="windows-cta" className="group inline-flex items-center gap-3 mt-10 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-label font-semibold hover:bg-accent transition-colors">
                Book rådgivning <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Section 3 — Vindunor partnership */}
      <section className="bg-foreground text-background py-24 md:py-32" data-testid="vindunor-section">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-label text-accent mb-6">Vores samarbejde med Vindunor</p>
            <h2 className="font-display text-4xl md:text-5xl font-light leading-tight text-balance">
              Danskproducerede vinduer valgt med samme blik for helheden
            </h2>
            <div className="mt-8 space-y-5 text-background/75 font-light text-lg max-w-2xl">
              <p>Vi samarbejder med den familieejede vinduesfabrik Vindunor, fordi deres tilgang passer til vores egen. Alle vinduer produceres efter mål i Nordjylland med fokus på dansk kvalitet, nordisk æstetik og gennemtænkte detaljer.</p>
              <p>Det brede udvalg i træ og træ/alu giver os frihed til at anbefale den løsning, der passer bedst til husets arkitektur, dine ønsker og behovet for vedligeholdelse — frem for at presse boligen ind i en standardløsning.</p>
            </div>
            <Link
              to="/kontakt"
              data-testid="vindunor-cta"
              data-track="cta_click"
              className="group inline-flex items-center gap-3 mt-10 bg-background text-foreground px-8 py-4 text-xs uppercase tracking-label font-semibold hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Book rådgivning <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="md:col-span-5 md:col-start-8">
            <a
              href="https://vindunor.dk/inspiration/kataloger-og-produktblade/"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="vindunor-logo-link"
              className="group block bg-background text-foreground border border-background/10 hover:shadow-2xl transition-shadow"
            >
              <div className="px-10 py-16 md:py-20 flex items-center justify-center">
                <img
                  src="/images/vindunor-logo.png"
                  alt="Vindunor logo"
                  className="w-full max-w-[280px] md:max-w-[320px] h-auto"
                />
              </div>
              <div className="border-t border-foreground/10 px-6 py-4 flex items-center justify-between gap-4 text-xs uppercase tracking-label text-foreground/70 group-hover:text-foreground transition-colors">
                <span>Se katalog & produktblade</span>
                <ArrowUpRight size={16} weight="light" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </a>
            <p className="mt-4 text-xs text-background/50 font-light">Tryk på logoet for at se Vindunors kataloger og produktblade.</p>
          </div>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32">
        <FadeUp>
          <blockquote className="font-display text-3xl md:text-5xl font-light leading-[1.15] max-w-4xl text-balance">
            “Vinduerne skal ikke bare passe i åbningerne — de skal føles som en naturlig del af huset.”
          </blockquote>
        </FadeUp>
      </section>
    </div>
  );
}
